import React, {
  RefObject,
  useContext,
  useEffect,
  useState,
} from 'react';
import SearchIcon from 'core/icons/search';
import SettingsIcon from 'core/icons/settings';
import {
  maxBillingCodeSearchQueryLength,
} from 'core/constants';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import SummaryCard, {
  SummaryCardItem,
} from 'core/components/SummaryCard';
import Filters from 'core/Filters';
import Button from 'pages/Dashboard/components/Button';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import {
  useFilterQueryWithDateRange,
  useMedia,
  useTableReset,
} from 'core/hooks';
import useStorageState from 'core/hooks/storage';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import Table from 'core/components/VirtualTable';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import DataProvider, {
  DataContext,
} from 'core/context/data';
import {
  PaginationState as ReactTablePaginationState,
} from '@tanstack/react-table';
import compact from 'lodash/compact';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import {
  RTMPatientRowDTO,
  RTMPatientsFilterQueryDTO,
  RTMPatientsRequestDTO,
  RTMPatientsResponseDTO,
  RTMPatientsSummaryDTO,
} from 'dtos';
import {
  RTMMonitoringPriority,
} from 'dtos/rTMMonitoringPriority';
import {
  postApiRTMMonitoringGetPatients,
} from 'endpoints';
import {
  serializeDateRange,
} from 'pages/Dashboard/transport/dateRange';
import columns from 'pages/Dashboard/pages/Dashboard/RTM/columns';
import {
  rtmEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import isNil from 'lodash/isNil';

const SUMMARY_CARD_PRIORITY_MAP: Record<string, string> = {
  needsReview: RTMMonitoringPriority.NeedsReview,
  watch: RTMMonitoringPriority.Watch,
  stable: RTMMonitoringPriority.Stable,
};

type OverviewProps = {
  tableInstanceRef: RefObject<VirtualTableInstance>;
  selectedId: string | null;
  onSelect: (id: string) => void;
};

function getSummaryItems(summary?: RTMPatientsSummaryDTO): SummaryCardItem[] {
  return [
    {
      id: 'all',
      label: 'All',
      value: summary?.total ?? 0,
      tone: 'default',
    },
    {
      id: 'needsReview',
      label: 'Needs Review',
      value: summary?.needsReview ?? 0,
      tone: 'danger',
    },
    {
      id: 'watch',
      label: 'Watch',
      value: summary?.watch ?? 0,
      tone: 'warning',
    },
    {
      id: 'stable',
      label: 'Stable',
      value: summary?.stable ?? 0,
      tone: 'info',
    },
  ];
}

function getPriorityList(selectedId: string | null): string[] | undefined {
  if (isNil(selectedId)) {
    return undefined;
  }
  const priority = SUMMARY_CARD_PRIORITY_MAP[selectedId];
  return isNil(priority) ? undefined : [priority];
}

function Overview({
  tableInstanceRef,
  selectedId,
  onSelect,
}: OverviewProps) {
  const {
    data,
    isFetched,
    auxiliary,
    refetch,
  } = useContext(DataContext);
  const { requestMedia } = useMedia();
  const { canRequestMultimedia } = useEMRAccess();

  const [summary, setSummary] = useState<RTMPatientsSummaryDTO | undefined>();
  const items = getSummaryItems(summary);

  useEffect(() => {
    if (!isNil(auxiliary)) {
      setSummary(auxiliary as RTMPatientsSummaryDTO);
    }
  }, [auxiliary]);

  useEffect(() => {
    if (isFetched && !isNil(data) && canRequestMultimedia) {
      const patientIds = compact(
        (data ?? []).map(({ patient }) => patient?.patientId),
      );
      if (patientIds.length > 0) {
        requestMedia(patientIds);
      }

      const userIds = compact(
        (data ?? []).flatMap(({ orderingProvider, reviewedBy }) => [
          orderingProvider?.userId,
          reviewedBy?.userId,
        ]),
      );
      if (userIds.length > 0) {
        requestMedia(userIds, MediaOriginatorType.User);
      }
    }
  }, [isFetched, data, canRequestMultimedia, requestMedia]);

  useEffect(() => {
    const unsubscribe = rtmEventChannel.on('onRTMPatientDataChange', () => {
      refetch?.();
    });
    return unsubscribe;
  }, [refetch]);

  return (
    <div className="relative flex flex-col flex-1 overflow-y-hidden text-gray-600">
      <div className="px-4 pb-3">
        <SummaryCard
          items={items}
          selectedId={selectedId}
          onSelect={onSelect}
          isLoading={isNil(summary)}
        />
      </div>
      <div className="w-full flex-1 min-h-0">
        <Table<RTMPatientRowDTO>
          instanceRef={tableInstanceRef}
          columns={columns}
          getRowId={(row) => row.patient?.patientId ?? 0}
          emptySlot='No RTM patients found'
          loadingSlot="Loading RTM patients..."
          classes={{
            header: 'px-4',
            outerRow: '!w-[calc(100%-16px)] left-4',
            cell: '!py-2 !min-h-[56px]',
          }}
          onRowClick={(_table, row) => {
            const patientId = row.original?.patient?.patientId;
            if (!isNil(patientId)) {
              rtmEventChannel.emit('onShowRTMPatientTrends', {
                patient: row.original.patient as PatientCompact,
              });
            }
          }}
        />
      </div>
    </div>
  );
}

export default function RTM() {
  const userInfo = useUserInfo();
  const {
    reset,
    tableInstanceRef,
  } = useTableReset();

  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery({ storageKey: 'rtmSearchQuery' });
  const [selectedId, setSelectedId] = useStorageState<string | null>('rtmSelectedTab', 'all');
  const {
    filterQuery,
    setFilterQuery,
    filterInstanceRef,
    dateRange,
    setDateRange,
  } = useFilterQueryWithDateRange<RTMPatientsFilterQueryDTO>(
    'rtmMonitoring',
    { uniqueKey: 'rtmDateRange' },
    true,
  );

  const onFilterUpdate = () => {
    setFilterQuery(filterInstanceRef?.current?.getFilterQuery?.() ?? {});
    reset();
  };

  const onSelectSummaryCard = (id: string) => {
    setSelectedId(id);
    reset();
  };

  const [defaultPagination] = useStorageState<ReactTablePaginationState>('rtmPagination', {
    pageSize: 25,
    pageIndex: 0,
  });
  const canShowThresholdButton = userInfo?.flags?.hasRTMThresholdAccess ?? false;

  return (
    <div className="flex h-full min-w-0 w-full flex-col gap-4">
      <div className="min-w-0 w-full shrink-0 overflow-x-auto px-4 py-2 -my-2">
        <div className="flex w-full min-w-max items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <SearchInput
              placeholder="Enter patient name, phone or date of birth"
              className="w-96"
              delay={400}
              value={searchQuery}
              onChange={(val) => {
                setSearchQuery(val);
                reset();
              }}
              inputProps={{ autoComplete: 'off', maxLength: maxBillingCodeSearchQueryLength }}
              icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
              id="rtm-patient-search"
            />
            <DateRangePicker
              maxDate={null}
              value={dateRange}
              onChange={(nextDateRange) => {
                if (nextDateRange) {
                  setDateRange(nextDateRange);
                  reset();
                }
              }}
            />
            <Filters
              pageObject="rtmMonitoring"
              onFilterUpdate={onFilterUpdate}
              instanceRef={filterInstanceRef}
              useStorage
            />
          </div>
          {canShowThresholdButton && (
            <Button
              variant="outlined"
              className="!h-8"
              onClick={() => rtmEventChannel.emit('onShowRTMThresholdSettings')}
              startIcon={<SettingsIcon className="!w-4 !h-4 !text-inherit" />}
            >
              Threshold
            </Button>
          )}
        </div>
      </div>
      <DataProvider<RTMPatientsResponseDTO, RTMPatientRowDTO, RTMPatientsSummaryDTO>
        queryKey="rtm-patients"
        pagination={defaultPagination}
        getData={(request: RTMPatientsRequestDTO) => postApiRTMMonitoringGetPatients(request)}
        getMeta={(response) => (response?.patients?.pageProperties ?? {})}
        getAuxiliary={(response) => response?.summary}
        getQuery={({
          dateRange: queryDateRange,
          searchQuery: querySearch,
          filterQuery: queryFilter,
          selectedId: querySelectedId,
          pagination,
        }) => ({
          dateRange: serializeDateRange(queryDateRange),
          searchText: querySearch,
          priorityList: getPriorityList(querySelectedId),
          filterQuery: queryFilter,
          pageRequest: {
            page: pagination?.pageIndex ?? 0,
            pageSize: pagination?.pageSize ?? 25,
          },
        } satisfies RTMPatientsRequestDTO)}
        normalize={(response) => response?.patients?.data ?? []}
        params={{
          dateRange,
          searchQuery,
          filterQuery,
          selectedId,
        }}
      >
        <Overview
          tableInstanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
          selectedId={selectedId}
          onSelect={onSelectSummaryCard}
        />
      </DataProvider>
    </div>
  );
}
